import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ActivityFormData, EMPTY_ACTIVITY_FORM } from "../types/activity";
import { API_URL } from "../config";

const AddActivity = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ActivityFormData>({ ...EMPTY_ACTIVITY_FORM });
  const [isLoading, setIsLoading] = useState(false);

  const [inclusionInput, setInclusionInput] = useState("");
  const [itiTime, setItiTime] = useState("");
  const [itiLabel, setItiLabel] = useState("");

  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);

  const token = localStorage.getItem("token_admin_silverjarz");

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ── Inclusions ──────────────────────────────────────────────
  const handleAddInclusion = () => {
    if (!inclusionInput.trim()) {
      toast.error("Inclusion text is required");
      return;
    }
    setFormData((prev) => ({ ...prev, inclusions: [...prev.inclusions, inclusionInput.trim()] }));
    setInclusionInput("");
  };

  const handleRemoveInclusion = (index: number) => {
    setFormData((prev) => ({ ...prev, inclusions: prev.inclusions.filter((_, i) => i !== index) }));
  };

  // ── Itinerary ───────────────────────────────────────────────
  const handleAddItineraryStep = () => {
    if (!itiTime.trim() || !itiLabel.trim()) {
      toast.error("Time and label are required for an itinerary step");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      itinerary: [...prev.itinerary, { time: itiTime.trim(), label: itiLabel.trim() }],
    }));
    setItiTime("");
    setItiLabel("");
  };

  const handleRemoveItineraryStep = (index: number) => {
    setFormData((prev) => ({ ...prev, itinerary: prev.itinerary.filter((_, i) => i !== index) }));
  };

  // ── Photos ──────────────────────────────────────────────────
  const handlePhotosChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setPhotos((prev) => [...prev, ...files]);
    setPhotoPreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
    e.target.value = "";
  };

  const handleRemovePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Submit ──────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.description || !formData.price) {
      toast.error("Please fill in all required fields before saving");
      return;
    }

    setIsLoading(true);
    try {
      const payload = new FormData();
      payload.append("name", formData.name);
      payload.append("description", formData.description);
      payload.append("price", formData.price);

      // Backend attend un objet { "10:00": "Departure", ... }
      const itineraryObject = formData.itinerary.reduce((acc, step) => {
        acc[step.time] = step.label;
        return acc;
      }, {} as Record<string, string>);

      payload.append("inclusions", JSON.stringify(formData.inclusions));
      payload.append("itinerary", JSON.stringify(itineraryObject));

      photos.forEach((file) => payload.append("photos[]", file));

      const response = await axios.post(`${API_URL}/activities`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 201) {
        toast.success("Activity created successfully!");
        navigate("/admin/list-activities");
      }
    } catch (error: any) {
      console.error("Error creating activity:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Error creating activity");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .act-container { max-width: 1280px; margin: 0 auto; }
        .act-card { background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0 !important; box-shadow: 0 4px 16px -2px rgba(0, 0, 0, 0.03); margin-bottom: 24px; transition: border-color 0.2s ease; }
        .act-card:hover { border-color: #cbd5e1; }
        .act-card-head { display: flex; align-items: center; gap: 14px; padding: 20px 24px; background: #ffffff; border-bottom: 1px solid #f1f5f9; }
        .act-card-icon { width: 40px; height: 40px; border-radius: 10px; background: rgba(41, 75, 90, 0.08); color: #294b5a; display: flex; align-items: center; justify-content: center; font-size: 15px; flex-shrink: 0; }
        .act-card-title { font-size: 15px; font-weight: 700; color: #294b5a; letter-spacing: -0.01em; }
        .act-card-sub { font-size: 12.5px; color: #64748b; margin-top: 2px; }
        .act-card-body { padding: 24px; }
        .act-label { display: block; font-size: 12.5px; font-weight: 600; color: #334155; margin-bottom: 7px; }
        .act-label .req { color: #dc2626; margin-left: 2px; }
        .act-input-wrap { position: relative; }
        .act-input-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 13px; pointer-events: none; }
        .act-input, .act-textarea { width: 100%; font-size: 13.5px; color: #0f172a; border: 1px solid #cbd5e1 !important; border-radius: 8px; background: #f8fafc; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
        .act-input { height: 42px; padding: 0 14px 0 38px; }
        .act-input--no-icon { padding-left: 14px; }
        .act-textarea { padding: 12px 14px; resize: vertical; line-height: 1.5; min-height: 90px; }
        .act-input::placeholder, .act-textarea::placeholder { color: #94a3b8; }
        .act-input:focus, .act-textarea:focus { outline: none; border-color: #294b5a; background: #ffffff; box-shadow: 0 0 0 3px rgba(41, 75, 90, 0.1); }
        .act-fee-currency { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); font-size: 12.5px; font-weight: 700; color: #294b5a; pointer-events: none; }
        .act-fee-input { padding-left: 40px !important; font-weight: 600; }
        .act-doc-add { display: flex; gap: 10px; flex-wrap: wrap; }
        .act-doc-add-btn { height: 42px; padding: 0 20px; border-radius: 8px; border: none; background: #294b5a; color: #ffffff; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; white-space: nowrap; transition: all 0.2s ease; }
        .act-doc-add-btn:hover { background: #1e3743; }
        .act-doc-list { margin-top: 16px; border: 1px solid #e2e8f0 !important; border-radius: 10px; overflow: hidden; }
        .act-doc-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; font-size: 13.5px; color: #1e293b; background: #ffffff; }
        .act-doc-row:hover { background: #f8fafc; }
        .act-doc-row + .act-doc-row { border-top: 1px solid #f1f5f9; }
        .act-doc-num { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: rgba(211, 190, 118, 0.2); color: #8c7329; font-size: 11px; font-weight: 700; margin-right: 12px; flex-shrink: 0; }
        .act-doc-remove { width: 30px; height: 30px; border-radius: 6px; border: 1px solid transparent !important; background: transparent; color: #94a3b8; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s ease; }
        .act-doc-remove:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }
        .act-sidebar-card { position: sticky; top: 20px; background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0 !important; box-shadow: 0 4px 16px -2px rgba(0, 0, 0, 0.04); padding: 24px; }
        .act-summary-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px dashed #e2e8f0; font-size: 13px; }
        .act-summary-item:last-child { border-bottom: none; }
        .act-btn { height: 42px; padding: 0 24px; font-size: 13.5px; font-weight: 600; border-radius: 8px; cursor: pointer; border: none; display: inline-flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); text-decoration: none; }
        .act-btn--ghost { background: #ffffff; color: #475569; border: 1px solid #cbd5e1 !important; }
        .act-btn--ghost:hover { background: #f8fafc; color: #0f172a; border-color: #94a3b8; }
        .act-btn--primary { background: #294b5a; color: #ffffff; box-shadow: 0 2px 8px rgba(41, 75, 90, 0.25); width: 100%; }
        .act-btn--primary:hover:not(:disabled) { background: #1e3743; box-shadow: 0 4px 12px rgba(41, 75, 90, 0.35); }
        .act-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .act-btn--primary i { color: #d3be76; }
        .act-photo-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: 12px; margin-top: 16px; }
        .act-photo-thumb { position: relative; width: 100%; aspect-ratio: 1 / 1; border-radius: 8px; overflow: hidden; border: 1px solid #e2e8f0; }
        .act-photo-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .act-photo-remove { position: absolute; top: 4px; right: 4px; width: 22px; height: 22px; border-radius: 50%; border: none; background: rgba(15, 23, 42, 0.65); color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; }
        .act-photo-remove:hover { background: #dc2626; }
        .act-upload-zone { border: 2px dashed #cbd5e1; border-radius: 10px; padding: 24px; text-align: center; cursor: pointer; transition: all 0.15s ease; background: #f8fafc; }
        .act-upload-zone:hover { border-color: #294b5a; background: #ffffff; }
      `}</style>

      <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "24px 20px 40px", boxSizing: "border-box" }}>
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="act-container">
          <div className="d-flex justify-content-between align-items-end mb-4">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: "13px", color: "#64748b" }}>
                <Link to="/admin/list-activities" style={{ color: "#64748b", textDecoration: "none", fontWeight: 500 }}>Activities</Link>
                <i className="fa-solid fa-chevron-right" style={{ fontSize: "10px", color: "#94a3b8" }}></i>
                <span style={{ color: "#294b5a", fontWeight: 600 }}>Add New Activity</span>
              </div>
              <h1 style={{ color: "#294b5a", fontSize: "22px", fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>Create New Activity</h1>
              <p style={{ color: "#64748b", fontSize: "13.5px", margin: "4px 0 0" }}>Fill in the details, inclusions, itinerary and photos.</p>
            </div>
            <Link to="/admin/list-activities" className="act-btn act-btn--ghost">
              <i className="fa-solid fa-arrow-left"></i>
              Back to Activities
            </Link>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              <div className="col-12 col-lg-8">
                {/* BASIC INFO */}
                <div className="act-card">
                  <div className="act-card-head">
                    <div className="act-card-icon"><i className="fa-solid fa-compass"></i></div>
                    <div>
                      <div className="act-card-title">Basic Information</div>
                      <div className="act-card-sub">Name, description and pricing</div>
                    </div>
                  </div>
                  <div className="act-card-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="act-label">Activity Name<span className="req">*</span></label>
                        <div className="act-input-wrap">
                          <i className="fa-solid fa-heading act-input-icon"></i>
                          <input type="text" name="name" className="act-input" placeholder="e.g. Beach Camping"
                            value={formData.name} onChange={handleInputChange} required />
                        </div>
                      </div>

                      <div className="col-12">
                        <label className="act-label">Description<span className="req">*</span></label>
                        <textarea name="description" className="act-textarea" placeholder="Full description of the activity..."
                          value={formData.description} onChange={handleInputChange} required />
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="act-label">Price<span className="req">*</span></label>
                        <div className="act-input-wrap">
                          <span className="act-fee-currency">AED</span>
                          <input type="number" name="price" className="act-input act-fee-input" placeholder="0.00" step="0.01" min="0"
                            value={formData.price} onChange={handleInputChange} required />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* INCLUSIONS */}
                <div className="act-card">
                  <div className="act-card-head">
                    <div className="act-card-icon"><i className="fa-solid fa-list-check"></i></div>
                    <div>
                      <div className="act-card-title">Inclusions</div>
                      <div className="act-card-sub">What's included in this activity</div>
                    </div>
                  </div>
                  <div className="act-card-body">
                    <div className="act-doc-add">
                      <div className="act-input-wrap" style={{ flex: 1, minWidth: 220 }}>
                        <i className="fa-solid fa-check act-input-icon"></i>
                        <input type="text" className="act-input" placeholder="e.g. BBQ Dinner"
                          value={inclusionInput} onChange={(e) => setInclusionInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddInclusion(); } }} />
                      </div>
                      <button type="button" onClick={handleAddInclusion} className="act-doc-add-btn">
                        <i className="fa-solid fa-plus"></i>
                        Add Inclusion
                      </button>
                    </div>

                    <div className="act-doc-list">
                      {formData.inclusions.length > 0 ? (
                        formData.inclusions.map((item, index) => (
                          <div key={index} className="act-doc-row">
                            <div className="d-flex align-items-center">
                              <span className="act-doc-num">{index + 1}</span>
                              <span style={{ fontWeight: 500 }}>{item}</span>
                            </div>
                            <button type="button" onClick={() => handleRemoveInclusion(index)} className="act-doc-remove" title="Remove">
                              <i className="fa-solid fa-trash-can"></i>
                            </button>
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: "32px 16px", textAlign: "center", color: "#94a3b8", fontSize: "13.5px" }}>
                          No inclusions added yet.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ITINERARY */}
                <div className="act-card">
                  <div className="act-card-head">
                    <div className="act-card-icon"><i className="fa-solid fa-route"></i></div>
                    <div>
                      <div className="act-card-title">Itinerary</div>
                      <div className="act-card-sub">Step by step schedule of the activity</div>
                    </div>
                  </div>
                  <div className="act-card-body">
                    <div className="act-doc-add">
                      <div className="act-input-wrap" style={{ width: 130 }}>
                        <i className="fa-solid fa-clock act-input-icon"></i>
                        <input type="text" className="act-input" placeholder="10:00"
                          value={itiTime} onChange={(e) => setItiTime(e.target.value)} />
                      </div>
                      <div className="act-input-wrap" style={{ flex: 1, minWidth: 220 }}>
                        <i className="fa-solid fa-flag act-input-icon"></i>
                        <input type="text" className="act-input" placeholder="e.g. Departure"
                          value={itiLabel} onChange={(e) => setItiLabel(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddItineraryStep(); } }} />
                      </div>
                      <button type="button" onClick={handleAddItineraryStep} className="act-doc-add-btn">
                        <i className="fa-solid fa-plus"></i>
                        Add Step
                      </button>
                    </div>

                    <div className="act-doc-list">
                      {formData.itinerary.length > 0 ? (
                        formData.itinerary.map((step, index) => (
                          <div key={index} className="act-doc-row">
                            <div className="d-flex align-items-center">
                              <span className="act-doc-num">{index + 1}</span>
                              <span style={{ fontWeight: 700, color: "#294b5a", marginRight: 10 }}>{step.time}</span>
                              <span style={{ fontWeight: 500 }}>{step.label}</span>
                            </div>
                            <button type="button" onClick={() => handleRemoveItineraryStep(index)} className="act-doc-remove" title="Remove">
                              <i className="fa-solid fa-trash-can"></i>
                            </button>
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: "32px 16px", textAlign: "center", color: "#94a3b8", fontSize: "13.5px" }}>
                          No itinerary steps added yet.
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* PHOTOS */}
                <div className="act-card">
                  <div className="act-card-head">
                    <div className="act-card-icon"><i className="fa-solid fa-images"></i></div>
                    <div>
                      <div className="act-card-title">Photos</div>
                      <div className="act-card-sub">Upload one or more photos for this activity</div>
                    </div>
                  </div>
                  <div className="act-card-body">
                    <label htmlFor="photo-upload" className="act-upload-zone">
                      <i className="fa-solid fa-cloud-arrow-up mb-2" style={{ fontSize: 22, color: "#294b5a", display: "block" }}></i>
                      <span style={{ fontSize: 13.5, color: "#475569", fontWeight: 600 }}>Click to upload photos</span>
                      <input id="photo-upload" type="file" accept="image/*" multiple onChange={handlePhotosChange} style={{ display: "none" }} />
                    </label>

                    {photoPreviews.length > 0 && (
                      <div className="act-photo-grid">
                        {photoPreviews.map((src, index) => (
                          <div key={index} className="act-photo-thumb">
                            <img src={src} alt={`preview-${index}`} />
                            <button type="button" className="act-photo-remove" onClick={() => handleRemovePhoto(index)}>
                              <i className="fa-solid fa-xmark"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* SIDEBAR */}
              <div className="col-12 col-lg-4">
                <div className="act-sidebar-card">
                  <div style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#94a3b8", marginBottom: "12px" }}>
                    Summary
                  </div>

                  <div style={{ backgroundColor: "#f8fafc", borderRadius: "8px", padding: "16px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
                    <div className="act-summary-item">
                      <span style={{ color: "#64748b" }}>Name</span>
                      <span style={{ fontWeight: 600, color: "#0f172a", maxWidth: "150px", textAlign: "right" }} className="text-truncate">
                        {formData.name || "—"}
                      </span>
                    </div>
                    <div className="act-summary-item">
                      <span style={{ color: "#64748b" }}>Price</span>
                      <span style={{ fontWeight: 600, color: "#294b5a" }}>{formData.price ? `${formData.price} AED` : "—"}</span>
                    </div>
                    <div className="act-summary-item">
                      <span style={{ color: "#64748b" }}>Inclusions</span>
                      <span style={{ fontWeight: 700, backgroundColor: "rgba(211,190,118,0.2)", color: "#8c7329", padding: "2px 8px", borderRadius: "10px", fontSize: "11px" }}>
                        {formData.inclusions.length}
                      </span>
                    </div>
                    <div className="act-summary-item">
                      <span style={{ color: "#64748b" }}>Itinerary Steps</span>
                      <span style={{ fontWeight: 600, color: "#0f172a" }}>{formData.itinerary.length}</span>
                    </div>
                    <div className="act-summary-item">
                      <span style={{ color: "#64748b" }}>Photos</span>
                      <span style={{ fontWeight: 600, color: "#0f172a" }}>{photos.length}</span>
                    </div>
                  </div>

                  <button type="submit" disabled={isLoading} className="act-btn act-btn--primary mb-2">
                    <i className="fa-solid fa-check"></i>
                    {isLoading ? "Saving Activity..." : "Save Activity"}
                  </button>

                  <Link to="/admin/list-activities" className="act-btn act-btn--ghost" style={{ width: "100%" }}>
                    Cancel
                  </Link>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddActivity;